import { useEffect } from "react";
import { nodeAt, useGameStore } from "../../app/gameStore";
import { useInteractionStore } from "../../render/iso/interactionStore";
import { Card } from "./Card";
import { getCardView, type CardView } from "./cardViews";
import { useDragStore } from "./dragStore";
import "./cards.css";

const CARD_SPACING = 132;
const ARC_LIFT = 16;
const ARC_ROTATION = 4;

/**
 * The player's hand, fanned along a shallow arc — now the engine's real hand,
 * dealt from the deck, rather than a fixed prop list.
 *
 * Where a card lands depends on how it is aimed (see cardViews.targetOf):
 * hardware onto an empty tile, attachments onto existing machines, directives
 * instantly, and pipes onto a machine and then a second one.
 */
export function Hand() {
  const snapshot = useGameStore((s) => s.snapshot);
  const playCardAt = useGameStore((s) => s.playCardAt);
  const playInstant = useGameStore((s) => s.playInstant);
  const beginPipe = useGameStore((s) => s.beginPipe);
  const reject = useGameStore((s) => s.reject);

  const dragCard = useDragStore((s) => s.card);
  const begin = useDragStore((s) => s.begin);
  const move = useDragStore((s) => s.move);
  const end = useDragStore((s) => s.end);

  const hoveredTile = useInteractionStore((s) => s.hoveredTile);
  const setDraggedCard = useInteractionStore((s) => s.setDraggedCard);

  const energyLeft = snapshot.energyBudgetKw - snapshot.energySpentKw;
  // Cards are only legal in Play; the hand stays visible the rest of the turn
  // so the player can read what they'll have, but it won't lift or drag.
  const canPlay = snapshot.phase === "play" && !snapshot.isRunComplete && !snapshot.isRunFailed;

  // Drag is tracked on window, not the card: the floating ghost is
  // pointer-events:none so the R3F canvas keeps receiving hover and can
  // keep the board's tile highlight in sync under the cursor.
  useEffect(() => {
    if (!dragCard) return;

    const onMove = (e: PointerEvent) => move(e.clientX, e.clientY);
    const onUp = () => {
      const tile = useInteractionStore.getState().hoveredTile;
      const view = getCardView(dragCard.id);
      const dragInstanceId = useDragStore.getState().instanceId;
      if (tile && view && dragInstanceId) {
        if (view.target === "pipe") {
          const node = nodeAt(useGameStore.getState().snapshot, tile);
          if (node) beginPipe(dragInstanceId, node.id);
          else reject("a pipe has to start at a machine");
        } else {
          playCardAt(dragInstanceId, tile);
        }
      }
      end();
      setDraggedCard(null, null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragCard, move, end, playCardAt, beginPipe, reject, setDraggedCard]);

  const handleDragStart = (card: CardView, instanceId: string, x: number, y: number) => {
    begin(card, instanceId, x, y);
    setDraggedCard(card.deploys ?? card.category, card.target);
  };

  // Each hand entry is now a real card instance, so the key is its
  // instanceId directly — no more position-based hack to disambiguate
  // duplicate ids (a deck holding two Cooling Jackets used to need one).
  const cards = snapshot.hand
    .map((instance) => {
      const view = getCardView(instance.cardId);
      if (!view) return null;
      let boundNodeLabel: string | null = null;
      if (instance.boundNodeId) {
        const boundNode = snapshot.nodes.find((n) => n.id === instance.boundNodeId);
        if (boundNode) {
          boundNodeLabel = `${boundNode.kind} ${instance.boundNodeId}`;
        }
      }
      return { view, instanceId: instance.instanceId, boundNodeLabel };
    })
    .filter((entry): entry is { view: CardView; instanceId: string; boundNodeLabel: string | null } => entry !== null);
  const mid = (cards.length - 1) / 2;

  return (
    <>
      <div className="hand">
        {cards.map(({ view, instanceId, boundNodeLabel }, i) => {
          const fromCenter = i - mid;
          return (
            <Card
              key={instanceId}
              card={view}
              instanceId={instanceId}
              offsetX={fromCenter * CARD_SPACING}
              lift={Math.abs(fromCenter) * ARC_LIFT}
              rotation={fromCenter * ARC_ROTATION}
              affordable={canPlay && view.costKw <= energyLeft}
              locked={snapshot.deckPenalties.lockedHandInstanceIds.includes(instanceId)}
              boundNodeLabel={boundNodeLabel}
              onDragStart={handleDragStart}
              onActivate={(card, instanceId) => playInstant(instanceId)}
            />
          );
        })}
      </div>

      {dragCard && <DragGhost />}
      {dragCard && hoveredTile && (
        <div className="drop-hint">
          {getCardView(dragCard.id)?.target === "pipe"
            ? `start the pipe at ${hoveredTile.x},${hoveredTile.y}`
            : `release to place on ${hoveredTile.x},${hoveredTile.y}`}
        </div>
      )}
    </>
  );
}

/** The card following the cursor. Never intercepts pointer events. */
function DragGhost() {
  const card = useDragStore((s) => s.card);
  const x = useDragStore((s) => s.x);
  const y = useDragStore((s) => s.y);
  if (!card) return null;
  return (
    <div className="drag-ghost" style={{ transform: `translate(${x}px, ${y}px)` }}>
      <div className="drag-ghost__inner">{card.name}</div>
    </div>
  );
}
